import axios from "axios";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

import Constants from "../App/Variables/Constants";
import firebaseApp from "../firebase/firebase";
import { getAuthToken } from "../helpers/AuthHelpers";

let messageListenerStarted = false;
let serviceWorkerListenerStarted = false;
let orderAlertAudio;

const getOrderAlertAudio = () => {
  if (!orderAlertAudio) {
    orderAlertAudio = new Audio("/order-alert.mp3");
    orderAlertAudio.preload = "auto";
    orderAlertAudio.volume = 0.85;
  }

  return orderAlertAudio;
};

const unlockOrderChime = async () => {
  const audio = getOrderAlertAudio();
  audio.muted = true;

  try {
    await audio.play();
  } finally {
    audio.pause();
    audio.currentTime = 0;
    audio.muted = false;
  }
};

const playOrderChime = async () => {
  const audio = getOrderAlertAudio();
  audio.currentTime = 0;
  await audio.play();
};

const handleOrderMessage = (payload) => {
  const title =
    payload?.notification?.title || payload?.data?.title || "New order received";
  const body =
    payload?.notification?.body ||
    payload?.data?.body ||
    "Open Orders to view it.";

  playOrderChime().catch((error) => {
    console.warn("[ORDER_PUSH][SOUND_FAILED]", error?.message);
  });
  window.dispatchEvent(
    new CustomEvent("newOrderNotification", {
      detail: {
        title,
        body,
        url: payload?.data?.url || "/orders",
      },
    }),
  );
};

const startForegroundListener = (messaging) => {
  if (messageListenerStarted) return;
  messageListenerStarted = true;

  onMessage(messaging, (payload) => {
    console.log("[ORDER_PUSH][FOREGROUND_MESSAGE]", payload);
    handleOrderMessage(payload);
  });
};

const startServiceWorkerListener = () => {
  if (serviceWorkerListenerStarted) return;
  serviceWorkerListenerStarted = true;

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "ORDER_BACKGROUND_PUSH" && event.data.payload) {
      console.log("[ORDER_PUSH][BACKGROUND_RELAY]", event.data.payload);
      handleOrderMessage(event.data.payload);
    }

    if (event.data?.type === "ORDER_NOTIFICATION_CLICK" && event.data.url) {
      window.location.assign(event.data.url);
    }
  });
};

export const getOrderNotificationToken = async ({ askPermission = false } = {}) => {
  if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
    throw new Error("Push notifications are not supported in this browser.");
  }

  let permission = Notification.permission;
  if (askPermission) {
    await unlockOrderChime();
  }
  if (askPermission && permission === "default") {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    return { token: "", permission };
  }

  if (!(await isSupported())) {
    throw new Error("Firebase push notifications are not supported in this browser.");
  }

  const registration = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js",
  );
  const messaging = getMessaging(firebaseApp);
  const token = await getToken(messaging, {
    serviceWorkerRegistration: registration,
  });

  if (!token) throw new Error("Firebase did not return a notification token.");

  return { token, permission, registration, messaging };
};

export const enableOrderNotifications = async ({ askPermission = false } = {}) => {
  const result = await getOrderNotificationToken({ askPermission });

  if (!result.token) {
    return { enabled: false, permission: result.permission };
  }

  await axios.post(
    Constants.postUrls.registerNotification,
    { token: result.token },
    { headers: { Authorization: getAuthToken() } },
  );

  startForegroundListener(result.messaging);
  startServiceWorkerListener();
  console.log("[ORDER_PUSH][READY]", {
    serviceWorkerScope: result.registration.scope,
    tokenPreview: `${result.token.slice(0, 16)}...`,
  });
  return { enabled: true, permission: result.permission };
};

export const initializeOrderNotifications = async () => {
  if (typeof Notification === "undefined") {
    return { enabled: false, permission: "unsupported" };
  }

  if (Notification.permission !== "granted") {
    return { enabled: false, permission: Notification.permission };
  }

  return enableOrderNotifications();
};
